import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import AppConst from '../apis/appconst';
import AddServiceGroup from './AddServiceGroup';

/**
 * ServiceGroups — list/edit/delete service groups.
 * Pure tab content (no page wrapper) — designed to render inside Services page tabs.
 */
function ServiceGroups({ isAdmin }) {
    const [groups, setGroups] = useState([]);
    const [allServices, setAllServices] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchKey, setSearchKey] = useState('');
    const [editItem, setEditItem] = useState(null);

    const fetchGroups = async () => {
        setIsLoading(true);
        try {
            const fd = new FormData();
            const resp = await axios.post(
                AppConst.BASE_URL + AppConst.getServiceGroupsUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            if (resp?.data?.status) {
                setGroups(resp.data.groups || []);
                setAllServices(resp.data.all_services || []);
            } else {
                setGroups([]);
                setAllServices([]);
            }
        } catch (e) {
            console.error(e);
            toast.error('Failed to load groups');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchGroups();
    }, []);

    const handleAddClick = () => {
        setEditItem(null);
        setTimeout(() => {
            document.getElementById('addgrpbt789')?.click();
        }, 50);
    };

    const handleEdit = (row) => {
        setEditItem(row);
        setTimeout(() => {
            document.getElementById('addgrpbt789')?.click();
        }, 50);
    };

    const handleDelete = async (row) => {
        if (!window.confirm(`Delete group "${row.cat_name}"? Services inside will be un-assigned (not deleted).`)) {
            return;
        }
        try {
            const fd = new FormData();
            fd.append('cat_id', row.cat_id);
            const resp = await axios.post(
                AppConst.BASE_URL + AppConst.delServiceGroupUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            if (resp?.data?.status) {
                toast.success('Group deleted');
                fetchGroups();
            } else {
                toast.error(resp?.data?.message || 'Failed to delete');
            }
        } catch (e) {
            console.error(e);
            toast.error('Failed to delete group');
        }
    };

    const filteredGroups = useMemo(() => {
        if (!searchKey.trim()) return groups;
        const k = searchKey.toLowerCase();
        return groups.filter(g =>
            (g.cat_name || '').toLowerCase().includes(k) ||
            (g.cat_type2 || '').toLowerCase().includes(k) ||
            (g.services || []).some(s => (s.cat_name || '').toLowerCase().includes(k))
        );
    }, [groups, searchKey]);

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#f1f5fb', color: '#405189',
                fontSize: '15px', fontWeight: '700',
                paddingLeft: '14px', paddingRight: '14px',
            }
        },
        rows: { style: { fontSize: '14px', minHeight: '58px' } },
        cells: { style: { paddingLeft: '14px', paddingRight: '14px', fontSize: '14px' } },
    };

    const columns = [
        {
            name: 'SN',
            selector: (row) => filteredGroups.indexOf(row) + 1,
            width: '70px',
        },
        {
            name: 'Group Name',
            cell: (row) => (
                <div>
                    <strong className="text-primary">
                        <i className="ri-folder-2-line me-1"></i>{row.cat_name}
                    </strong>
                    {row.cat_type2 && <div className="text-muted small">{row.cat_type2}</div>}
                </div>
            ),
            sortable: true,
            wrap: true,
        },
        {
            name: 'Services',
            cell: (row) => (
                <div style={{ padding: '8px 0', maxWidth: '500px' }}>
                    {!row.services || row.services.length === 0 ? (
                        <span className="text-muted small fst-italic">No services assigned</span>
                    ) : (
                        <div className="d-flex flex-wrap gap-1">
                            {row.services.map(s => (
                                <span key={s.cat_id} className="badge bg-light text-dark border" style={{ fontSize: '11px' }}>
                                    {s.cat_name}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            ),
            wrap: true,
        },


        {
            name: 'Location',
            cell: (row) => (
                <div style={{ padding: '4px 0' }}>
                    {row.cat_ward_name || row.cat_bed_name ? (
                        <div className="d-flex flex-column gap-1">
                            {row.cat_ward_name && (
                                <span className="badge bg-light text-dark border">
                                    <i className="ri-hospital-line me-1 text-primary"></i>
                                    {row.cat_ward_name}
                                </span>
                            )}
                            {row.cat_bed_name && (
                                <span className="badge bg-light text-dark border">
                                    <i className="ri-hotel-bed-line me-1 text-success"></i>
                                    {row.cat_bed_name}
                                </span>
                            )}
                        </div>
                    ) : (
                        <span className="text-muted small fst-italic">—</span>
                    )}
                </div>
            ),
            width: '200px',
            wrap: true,
        },

        {
            name: 'Count',
            selector: (row) => row.services_count || 0,
            width: '90px',
            cell: (row) => (
                <span className="badge bg-info">{row.services_count || 0}</span>
            )
        },
        {
            name: 'Action',
            width: '220px',
            cell: (row) => (
                <div className="d-flex gap-2">
                    <button
                        className="btn btn-sm btn-outline-primary"
                        disabled={!isAdmin}
                        style={{ opacity: !isAdmin ? 0.5 : 1 }}
                        onClick={() => handleEdit(row)}
                    >
                        <i className="ri-pencil-fill me-1"></i>Edit
                    </button>
                    <button
                        className="btn btn-sm btn-outline-danger"
                        disabled={!isAdmin}
                        style={{ opacity: !isAdmin ? 0.5 : 1 }}
                        onClick={() => handleDelete(row)}
                    >
                        <i className="ri-delete-bin-5-fill me-1"></i>Delete
                    </button>
                </div>
            ),
            ignoreRowClick: true,
        },
    ];

    return (
        <div>
            {/* Toolbar */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <div className="search-box" style={{ minWidth: '300px', flexGrow: 1, maxWidth: '500px' }}>
                    <input
                        className="form-control search"
                        placeholder="Search by group or service name..."
                        value={searchKey}
                        onChange={(e) => setSearchKey(e.target.value)}
                    />
                    <i className="ri-search-line search-icon"></i>
                </div>
                <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleAddClick}
                    disabled={!isAdmin}
                >
                    <i className="ri-add-line me-1"></i>Add Group
                </button>
            </div>

            {/* Summary */}
            <div className="alert alert-light border d-flex justify-content-between align-items-center py-2 mb-3">
                <span>
                    <i className="ri-information-line me-1 text-primary"></i>
                    <strong>{filteredGroups.length}</strong> group{filteredGroups.length !== 1 ? 's' : ''}
                    {' • '}
                    <strong>{allServices.length}</strong> total services available
                </span>
            </div>

            {/* Table */}
            <div className="table-responsive table-card mb-1">
                <DataTable
                    columns={columns}
                    data={filteredGroups}
                    pagination
                    paginationPerPage={20}
                    paginationRowsPerPageOptions={[5, 10, 20, 50]}
                    paginationComponentOptions={{
                        rowsPerPageText: 'Rows per page:',
                        rangeSeparatorText: 'of',
                    }}
                    highlightOnHover
                    responsive
                    persistTableHead
                    progressPending={isLoading}
                    customStyles={customStyles}
                    noHeader
                    noDataComponent={
                        <div className="text-center py-5 text-muted">
                            <i className="ri-folder-open-line" style={{ fontSize: '48px' }}></i>
                            <p className="mt-2 mb-0">
                                {searchKey ? `No groups match "${searchKey}"` : 'No service groups yet. Click "Add Group" to create one.'}
                            </p>
                        </div>
                    }
                />
            </div>

            {/* Modal */}
            <AddServiceGroup
                item={editItem}
                allServices={allServices}
                onSaved={fetchGroups}
            />
        </div>
    );
}

export default ServiceGroups;